import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";

// 🔹 Theme color options
export type ColorTheme =
    | "black-white"
    | "light-blue"
    | "midnight-blue"
    | "deep-wine"
    | "forest-green"
    | "ocean-breeze"
    | "green-palette";

const DEFAULT_COLOR_THEME: ColorTheme = "black-white";
const DEFAULT_DARK_MODE = true;
const SUPPORTED_COLOR_THEMES: ColorTheme[] = [
    "black-white",
    "light-blue",
    "midnight-blue",
    "deep-wine",
    "forest-green",
    "ocean-breeze",
    "green-palette",
];

// 🔹 Context shape
interface ThemeContextType {
    colorTheme: ColorTheme;
    isDarkMode: boolean;
    setColorTheme: (theme: ColorTheme) => void;
    setIsDarkMode: (value: boolean) => void;
    toggleDarkMode: () => void;
}

// 🔹 Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 🔹 Provider component
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const getInitialColorTheme = (): ColorTheme => {
        try {
            const storedTheme = localStorage.getItem("color-theme");
            if (storedTheme && SUPPORTED_COLOR_THEMES.includes(storedTheme as ColorTheme)) {
                return storedTheme as ColorTheme;
            }
            return DEFAULT_COLOR_THEME;
        } catch {
            return DEFAULT_COLOR_THEME;
        }
    };

    const getInitialDarkMode = (): boolean => {
        try {
            const stored = localStorage.getItem("dark-mode");
            return stored !== null
                ? stored === "true"
                : DEFAULT_DARK_MODE;
        } catch {
            return DEFAULT_DARK_MODE;
        }
    };

    const [colorTheme, setColorTheme] = useState<ColorTheme>(getInitialColorTheme);
    const [isDarkMode, setIsDarkMode] = useState<boolean>(getInitialDarkMode);

    const toggleDarkMode = () => setIsDarkMode(prev => !prev);

    useEffect(() => {
        const body = document.body;

        // Apply color theme
        body.setAttribute("data-theme", colorTheme);
        localStorage.setItem("color-theme", colorTheme);

        // Apply dark mode
        if (isDarkMode) {
            body.classList.add("dark");
        } else {
            body.classList.remove("dark");
        }

        localStorage.setItem("dark-mode", JSON.stringify(isDarkMode));
    }, [colorTheme, isDarkMode]);

    return (
        <ThemeContext.Provider
            value={{
                colorTheme,
                isDarkMode,
                setColorTheme,
                setIsDarkMode,
                toggleDarkMode,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

// 🔹 Hook for easy access
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};
